/**
 * Client-side derivation of the Top Posts `after` cursor for a given page.
 *
 * `topPosts` uses metric-sorted aggregation, so its Relay cursor encodes an
 * absolute offset rather than a seek key — it is `base64(String(offset))`.
 * Because the offset is fully determined by the page number, the client can
 * mint the cursor for ANY page directly, which lets the user jump to a
 * non-adjacent page (e.g. 1 → 5) instead of only stepping one page at a time.
 *
 * This intentionally mirrors the server encoder
 * (`services/core/core-api/src/graphql/modules/insights/lib/topPostsCursor.ts`
 * → `PageConnectionCursor.toToken`, which is `Buffer.from(str).toString('base64')`).
 * Frontend packages can't import the backend cursor util, so this is a local
 * copy per the frontend-boundary rule; keep the two in sync (drift is a
 * code-review concern). If the encodings ever diverge, the server's decode
 * validation degrades a bad cursor to offset 0 (page 1) rather than erroring.
 *
 * `after` semantics: the server reads `after` as the offset of the last item on
 * the previous page and starts strictly after it, so page P (1-indexed) maps to
 * `after = (P - 1) * perPage - 1`. Page 1 has no `after` (starts from the top).
 */
function encodeOffsetCursor(offset: number): string {
  return btoa(String(offset))
}

export function getTopPostsPageCursor(
  page: number,
  perPage: number,
): string | null {
  if (!Number.isInteger(page) || page <= 1) {
    return null
  }
  return encodeOffsetCursor((page - 1) * perPage - 1)
}
